import React from 'react';
import { motion } from 'framer-motion';
import { 
  FiSun, FiBattery, FiGrid, FiDollarSign, 
  FiActivity, FiInfo, FiHelpCircle
} from 'react-icons/fi';

import { NumericInputProps } from '../../types/components';
import Tooltip from './Tooltip';

/**
 * NumericInput component for editable numeric fields with formatting and tooltips
 * 
 * This component provides a styled input field for numeric values with
 * optional formatting, prefix/suffix, and tooltips.
 */
const NumericInput: React.FC<NumericInputProps> = ({ 
  label, 
  name, 
  value, 
  onChange, 
  format, 
  prefix = "", 
  suffix = "", 
  description = null,
  tooltipContent = null,
  disabled = false
}) => {
  /**
   * Get icon based on input type/name
   */
  const getInputIcon = () => {
    if (name.includes('Cost') || name.includes('cost')) return <FiDollarSign className="text-blue-500" />;
    if (name.includes('Rate') || name.includes('rate')) return <FiActivity className="text-blue-500" />;
    if (name.includes('Battery') || name.includes('battery')) return <FiBattery className="text-green-500" />;
    if (name.includes('Solar') || name.includes('solar')) return <FiSun className="text-yellow-500" />;
    if (name.includes('Grid') || name.includes('grid')) return <FiGrid className="text-blue-500" />;
    return null;
  };
  
  /**
   * Get color class based on input type/name
   */
  const getColorClass = () => {
    if (name.includes('Cost') || name.includes('cost')) return 'border-blue-200 hover:border-blue-400';
    if (name.includes('Rate') || name.includes('rate')) return 'border-indigo-200 hover:border-indigo-400';
    if (name.includes('Battery') || name.includes('battery')) return 'border-green-200 hover:border-green-400';
    if (name.includes('Solar') || name.includes('solar')) return 'border-yellow-200 hover:border-yellow-400';
    if (name.includes('Grid') || name.includes('grid')) return 'border-blue-200 hover:border-blue-400';
    return 'border-gray-200 hover:border-gray-400';
  };
  
  /**
   * Generate unique ID for input and label association
   */
  const inputId = `numeric-input-${name.replace(/[^\w]/g, '-')}`;
  
  return (
    <motion.div 
      className="mb-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex justify-between mb-1">
        <div className="flex items-center">
          {getInputIcon() && <span className="mr-2">{getInputIcon()}</span>}
          {tooltipContent ? (
            <Tooltip 
              title={"About " + label}
              content={tooltipContent}
            >
              <div className="flex items-center text-sm font-medium text-gray-700 cursor-help">
                <label htmlFor={inputId} className="tooltip-indicator">{label}</label>
                <FiInfo className="ml-1 text-blue-500 h-3.5 w-3.5" />
              </div>
            </Tooltip>
          ) : (
            <label htmlFor={inputId} className="text-sm font-medium text-gray-700">{label}</label>
          )}
        </div>
        {format && (
          <motion.span 
            className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            {format(value)}
          </motion.span>
        )}
      </div>
      <motion.div 
        className={`relative border ${getColorClass()} transition-colors duration-200 rounded-md`}
        whileHover={{ boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}
      >
        {prefix && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500 text-sm font-medium">{prefix}</span>
          </div>
        )}
        <input
          id={inputId}
          type="number"
          value={value}
          onChange={(e) => onChange(name, parseFloat(e.target.value) || 0)}
          className={`block w-full text-sm border-0 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 rounded-md ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-transparent'}`}
          style={{ 
            paddingLeft: prefix ? '2rem' : '1rem', 
            paddingRight: suffix ? '3rem' : '1rem' 
          }}
          aria-label={label}
          aria-describedby={description ? `${inputId}-description` : undefined}
          disabled={disabled}
        />
        {suffix && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <span className="text-gray-500 text-sm font-medium bg-gray-50 px-2 py-1 rounded-full">{suffix}</span>
          </div>
        )}
      </motion.div>
      {description && (
        <div className="mt-1 flex items-center">
          <p 
            className="text-xs text-gray-500"
            id={`${inputId}-description`}
          >
            {description}
          </p>
          {tooltipContent && (
            <motion.button
              className="ml-1 p-0.5 rounded-full text-gray-400 hover:text-gray-600 focus:outline-none"
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              aria-label="More information"
            >
              <FiHelpCircle className="h-3 w-3" />
            </motion.button>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default NumericInput;